import React, { ComponentType } from "react";
import Link from "next/link";
import Image from "next/image";
import { ChatBubbleOvalLeftIcon } from "@heroicons/react/24/outline";

export interface CapabilityCard {
	title?: string;
	items?: string[];
	imageSrc?: string;
	imageAlt?: string;
	type: "text" | "image";
}

interface CapabilitiesProps {
	title?: string;
	subtitle?: string;
	cards?: CapabilityCard[];
	ctaTitle?: string;
	ctaSubtitle?: string;
	ctaLink?: string;
	ctaText?: string;
	ctaIcon?: ComponentType<{ className?: string }>;
}

export const defaultCards: CapabilityCard[] = [
	{
		type: "text",
		title: "Product Strategy",
		items: [
			"Product leadership",
			"Roadmapping & prioritization",
			"Technical direction & architecture",
		],
	},
	{
		type: "text",
		title: "Design & UX",
		items: [
			"UI/UX design",
			"Design systems",
			"Experience optimization",
			"Accessibility & interaction design",
		],
	},
	{
		type: "image",
		imageSrc: "/images/CapabilitiesCard1.jpg",
		imageAlt: "Product development dashboard",
	},
	{
		type: "image",
		imageSrc: "/images/CapabilitiesCard2.jpg",
		imageAlt: "Mobile calendar application",
	},
	{
		type: "text",
		title: "Engineering",
		items: [
			"Frontend & backend development",
			"Mobile app development",
			"API & systems integration",
			"Performance & security optimization",
			"DevOps & infrastructure support",
		],
	},
	{
		type: "text",
		title: "QA & Reliability",
		items: [
			"Ticketed process",
			"Automated + manual QA",
			"Browser/device testing",
			"Release management",
		],
	},
	{
		type: "image",
		imageSrc: "/images/CapabilitiesCard3.jpg",
		imageAlt: "Mobile map application",
	},
	{
		type: "text",
		title: "Ongoing Support & Growth",
		items: [
			"On-demand sprints",
			"Continuous improvement",
			"Analytics, insights, iteration",
			"Rapid experimentation with AI-assisted workflows",
		],
	},
	{
		type: "image",
		imageSrc: "/images/CapabilitiesCard4.jpg",
		imageAlt: "Mobile gallery application",
	},
];

const Capabilities: React.FC<CapabilitiesProps> = ({
	title = "Your Entire Product & Engineering Function In One Scalable Partnership",
	subtitle = "You get a fully integrated team that covers every part of modern software development:",
	cards = defaultCards,
	ctaTitle = "All of this. One team. One monthly partnership.",
	ctaSubtitle = "No hiring. No babysitting. No fixed-bid traps.",
	ctaLink = "/contact",
	ctaText = "Let's Talk",
	ctaIcon: CtaIcon = ChatBubbleOvalLeftIcon,
}) => {
	return (
		<div className="CapabilitiesSection bg-white py-[clamp(50px,10.71vw,150px)] px-2 xl:px-0">
			<div className="container mx-auto max-w-6xl px-4 md:px-0">
				{/* Header Section */}
				<div className="text-center mb-12">
					{title && (
						<h2 className="text-[clamp(24px,2.57vw,36px)] font-bold leading-tight mb-4">{title}</h2>
					)}
					{subtitle && <p className="text-[clamp(18px,1.71vw,24px)]">{subtitle}</p>}
				</div>

				{/* Capabilities Grid */}
				<div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16">
					{cards.map((card, index) => (
						<div
							key={index}
							className="bg-white rounded-lg shadow-md overflow-hidden"
						>
							{card.type === "text" ? (
								<div className="p-5 md:p-6 h-full flex flex-col md:justify-between">
									{card.title && (
										<h3 className="text-[clamp(18px,1.71vw,24px)] font-bold mb-3 md:mb-auto">
											{card.title}
										</h3>
									)}
									{card.items && card.items.length > 0 && (
										<ul className="space-y-2">
											{card.items.map((item, itemIndex) => (
												<li
													key={itemIndex}
													className="flex items-start"
												>
													<span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-sm mt-2 mr-3"></span>
													<span className="leading-relaxed">{item}</span>
												</li>
											))}
										</ul>
									)}
								</div>
							) : (
								<div className="relative w-full h-full min-h-[300px]">
									{card.imageSrc ? (
										<Image
											src={card.imageSrc}
											alt={card.imageAlt || "Capability image"}
											layout="fill"
											objectFit="cover"
											className="object-cover rounded-lg"
										/>
									) : (
										<div className="w-full h-full bg-gray-200 flex items-center justify-center">
											<span className="text-gray-400">Image placeholder</span>
										</div>
									)}
								</div>
							)}
						</div>
					))}
				</div>

				{/* CTA Banner */}
				<div className="bg-blue rounded-lg p-4 md:p-6">
					<div className="flex flex-col md:flex-row items-center justify-between gap-6">
						<div className="flex-1 text-center md:text-left">
							{ctaTitle && (
								<h3 className="text-[clamp(20px,1.71vw,24px)] font-bold text-white mb-2">
									{ctaTitle}
								</h3>
							)}
							{ctaSubtitle && <p className="text-white">{ctaSubtitle}</p>}
						</div>
						<div className="flex-shrink-0">
							<Link href={ctaLink}>
								<a className="inline-flex items-center text-white font-semibold text-lg transition-colors">
									<CtaIcon className="w-6 h-6 mr-2" />
									{ctaText}
								</a>
							</Link>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Capabilities;
